/**
 * Web Bluetooth Mesh
 *
 * Tertiary transport fallback when all internet connectivity fails.
 * Uses the Capacitor BLE bridge for cross-platform support
 * (native iOS/Android via Capacitor plugin, web via Web Bluetooth API).
 */

import {
  isBLEAvailable,
  isBLEEnabled,
  initBLE,
  requestDevice,
  connect,
  disconnect,
  write,
  startNotifications,
  uint8ToDataView,
  dataViewToUint8,
  type BridgedBleDevice,
} from './capacitor-ble-bridge'

// Service UUIDs for CrowdTalkie Bluetooth mesh
const CROWDTALKIE_SERVICE_UUID = '00001234-0000-1000-8000-00805f9b34fb'
const MESSAGE_CHARACTERISTIC_UUID = '00001235-0000-1000-8000-00805f9b34fb'

export type BluetoothStatus = 'unavailable' | 'ready' | 'scanning' | 'connected'

export interface BluetoothPeer {
  id: string
  device: BridgedBleDevice
  lastSeen: Date
  /** Cleanup function for notification subscription */
  _stopNotify?: () => Promise<void>
}

let bluetoothStatus: BluetoothStatus = 'unavailable'
const connectedPeers = new Map<string, BluetoothPeer>()
let messageHandler: ((data: Uint8Array, fromPeerId: string) => void) | null = null

/**
 * Check if BLE is available (native or web)
 */
export function isBluetoothAvailable(): boolean {
  return isBLEAvailable()
}

/**
 * Initialize Bluetooth mesh
 */
export async function initializeBluetooth(): Promise<boolean> {
  if (!isBluetoothAvailable()) {
    console.log('[Bluetooth] BLE not available')
    bluetoothStatus = 'unavailable'
    return false
  }

  try {
    await initBLE()
    const enabled = await isBLEEnabled()
    bluetoothStatus = enabled ? 'ready' : 'unavailable'
    console.log('[Bluetooth] Initialized, enabled:', enabled)
    return enabled
  } catch (error) {
    console.error('[Bluetooth] Initialization failed:', error)
    bluetoothStatus = 'unavailable'
    return false
  }
}

/**
 * Get current Bluetooth status
 */
export function getBluetoothStatus(): BluetoothStatus {
  return bluetoothStatus
}

/**
 * Start scanning for nearby peers
 */
export async function startScanning(): Promise<void> {
  if (bluetoothStatus === 'unavailable') {
    throw new Error('Bluetooth not available')
  }

  bluetoothStatus = 'scanning'
  console.log('[Bluetooth] Starting scan for peers')

  try {
    const device = await requestDevice({
      services: [CROWDTALKIE_SERVICE_UUID],
      optionalServices: [CROWDTALKIE_SERVICE_UUID],
    })

    if (device) {
      await connectToPeer(device)
    }
  } catch (error) {
    if ((error as Error).name === 'NotFoundError') {
      console.log('[Bluetooth] No devices selected')
    } else {
      console.error('[Bluetooth] Scan failed:', error)
    }
  } finally {
    bluetoothStatus = connectedPeers.size > 0 ? 'connected' : 'ready'
  }
}

/**
 * Connect to a discovered peer
 */
async function connectToPeer(device: BridgedBleDevice): Promise<void> {
  console.log('[Bluetooth] Connecting to:', device.name || device.deviceId)

  try {
    await connect(device, (deviceId) => {
      console.log('[Bluetooth] Peer disconnected:', deviceId)
      const peer = connectedPeers.get(deviceId)
      peer?._stopNotify?.()
      connectedPeers.delete(deviceId)
      bluetoothStatus = connectedPeers.size > 0 ? 'connected' : 'ready'
    })

    const peer: BluetoothPeer = {
      id: device.deviceId,
      device,
      lastSeen: new Date(),
    }

    // Subscribe to message notifications
    const stopNotify = await startNotifications(
      device,
      CROWDTALKIE_SERVICE_UUID,
      MESSAGE_CHARACTERISTIC_UUID,
      (value: DataView) => {
        if (messageHandler) {
          const data = dataViewToUint8(value)
          messageHandler(data, device.deviceId)
        }
      }
    )
    peer._stopNotify = stopNotify

    connectedPeers.set(device.deviceId, peer)
    bluetoothStatus = 'connected'
    console.log('[Bluetooth] Connected to peer:', device.deviceId)
  } catch (error) {
    console.error('[Bluetooth] Connection failed:', error)
    throw error
  }
}

/**
 * Set message handler
 */
export function setMessageHandler(
  handler: (data: Uint8Array, fromPeerId: string) => void
): void {
  messageHandler = handler
}

/**
 * Send message to all connected peers
 */
export async function broadcastMessage(data: Uint8Array): Promise<void> {
  const dataView = uint8ToDataView(data)

  const promises = Array.from(connectedPeers.values()).map(async (peer) => {
    try {
      await write(
        peer.device,
        CROWDTALKIE_SERVICE_UUID,
        MESSAGE_CHARACTERISTIC_UUID,
        dataView
      )
    } catch (error) {
      console.error('[Bluetooth] Failed to send to peer:', peer.id, error)
    }
  })

  await Promise.allSettled(promises)
}

/**
 * Get connected peers
 */
export function getConnectedPeers(): BluetoothPeer[] {
  return Array.from(connectedPeers.values())
}

/**
 * Disconnect from all peers
 */
export function disconnectAll(): void {
  for (const peer of connectedPeers.values()) {
    peer._stopNotify?.()
    disconnect(peer.device).catch(() => {})
  }
  connectedPeers.clear()
  bluetoothStatus = 'ready'
  console.log('[Bluetooth] Disconnected from all peers')
}

/**
 * Stop Bluetooth mesh
 */
export function stopBluetooth(): void {
  disconnectAll()
  bluetoothStatus = 'unavailable'
  messageHandler = null
  console.log('[Bluetooth] Stopped')
}
